<template>
  <!-- Phase 2 - US-013: Fee Collection Report -->
  <div>
    <b-card header="Fee Collection Report" class="mb-3">
      <!-- Filters -->
      <b-row class="mb-3">
        <b-col cols="12" md="4">
          <date-picker
            v-model="filters.date_from"
            label="From Date"
          ></date-picker>
        </b-col>
        <b-col cols="12" md="4">
          <date-picker
            v-model="filters.date_to"
            label="To Date"
          ></date-picker>
        </b-col>
        <b-col cols="12" md="4" class="d-flex align-items-end">
          <b-button
            variant="primary"
            class="me-2"
            @click="loadReport"
            :disabled="!filters.date_from || !filters.date_to"
          >
            <b-icon icon="search"></b-icon> Generate Report
          </b-button>
          <b-button
            variant="outline-success"
            @click="exportReport"
            :disabled="reportData.length === 0"
          >
            <b-icon icon="download"></b-icon> Export
          </b-button>
        </b-col>
      </b-row>

      <!-- Loading -->
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner small></b-spinner> Loading...
      </div>

      <!-- Report Data -->
      <template v-else-if="reportData.length > 0">
        <!-- Summary Cards -->
        <b-row class="mb-3">
          <b-col cols="12" md="3">
            <div class="bg-light p-3 rounded text-center">
              <small class="text-muted">Total Collection</small>
              <h5 class="mb-0 text-success">
                Rs. {{ totalCollection.toLocaleString("en-IN") }}
              </h5>
            </div>
          </b-col>
          <b-col cols="12" md="3">
            <div class="bg-light p-3 rounded text-center">
              <small class="text-muted">Total Transactions</small>
              <h5 class="mb-0">{{ totalTransactions }}</h5>
            </div>
          </b-col>
          <b-col cols="12" md="3">
            <div class="bg-light p-3 rounded text-center">
              <small class="text-muted">Successful</small>
              <h5 class="mb-0 text-success">{{ totalSuccess }}</h5>
            </div>
          </b-col>
          <b-col cols="12" md="3">
            <div class="bg-light p-3 rounded text-center">
              <small class="text-muted">Failed</small>
              <h5 class="mb-0 text-danger">{{ totalFailed }}</h5>
            </div>
          </b-col>
        </b-row>

        <!-- Detail Table -->
        <b-table
          :items="reportData"
          :fields="fields"
          striped
          small
          responsive
          hover
          foot-clone
        >
          <template #cell(total_paid)="{ value }">
            Rs. {{ Number(value || 0).toLocaleString("en-IN") }}
          </template>
          <template #foot(payment_type_name)>
            <strong>Total</strong>
          </template>
          <template #foot(total_transactions)>
            <strong>{{ totalTransactions }}</strong>
          </template>
          <template #foot(success_count)>
            <strong>{{ totalSuccess }}</strong>
          </template>
          <template #foot(failure_count)>
            <strong>{{ totalFailed }}</strong>
          </template>
          <template #foot(total_paid)>
            <strong>Rs. {{ totalCollection.toLocaleString("en-IN") }}</strong>
          </template>
        </b-table>
      </template>

      <!-- No Data -->
      <div
        v-else-if="hasSearched"
        class="text-center py-3 text-muted"
      >
        No records found for the selected date range.
      </div>
    </b-card>
  </div>
</template>

<script>
import feeService from "../../assets/services/fee.service";
import DatePicker from "../form/DatePicker.vue";
import { getDateOnly } from "../../assets/js/common";

export default {
  name: "FeeReport",
  components: { DatePicker },
  data() {
    return {
      isLoading: false,
      hasSearched: false,
      reportData: [],
      filters: {
        date_from: "",
        date_to: getDateOnly(),
      },
    };
  },
  computed: {
    fields() {
      return [
        { key: "payment_type_name", label: "Fee Type" },
        { key: "total_transactions", label: "Total Transactions" },
        { key: "success_count", label: "Successful" },
        { key: "failure_count", label: "Failed" },
        { key: "total_paid", label: "Total Collected" },
      ];
    },
    totalCollection() {
      return this.reportData.reduce(
        (sum, r) => sum + parseFloat(r.total_paid || 0),
        0
      );
    },
    totalTransactions() {
      return this.reportData.reduce(
        (sum, r) => sum + parseInt(r.total_transactions || 0),
        0
      );
    },
    totalSuccess() {
      return this.reportData.reduce(
        (sum, r) => sum + parseInt(r.success_count || 0),
        0
      );
    },
    totalFailed() {
      return this.reportData.reduce(
        (sum, r) => sum + parseInt(r.failure_count || 0),
        0
      );
    },
  },
  methods: {
    async loadReport() {
      try {
        this.isLoading = true;
        this.hasSearched = true;
        let { data } = await feeService.getFeeReport(
          this.filters.date_from,
          this.filters.date_to
        );
        this.isLoading = false;
        if (data.status && data.list) {
          this.reportData = data.list;
        } else {
          this.reportData = [];
        }
      } catch (error) {
        this.isLoading = false;
        this.$alert.show(error.message);
      }
    },
    exportReport() {
      // Generate CSV export
      let csv = "Fee Type,Total Transactions,Successful,Failed,Total Collected\n";
      this.reportData.forEach((row) => {
        csv += `"${row.payment_type_name}",${row.total_transactions},${row.success_count},${row.failure_count},${row.total_paid}\n`;
      });
      csv += `"Total",${this.totalTransactions},${this.totalSuccess},${this.totalFailed},${this.totalCollection}\n`;

      let blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      let link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `fee_report_${this.filters.date_from}_to_${this.filters.date_to}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>
